import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import heroImage from '../assets/r13.webp';
import claimImage from '../assets/r12.png';
import FAQSection from './FAQSection';
import ConsultationCTA from '../components/ConsultationCTA';

const fadeIn = {
  hidden: { opacity: 0, y: 50 },
  visible: (i = 1) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.2, duration: 0.8, ease: 'easeOut' }
  })
};

const DenialManagement = () => {
  return (
    <>
      {/* SEO Meta Tags */}
      <Helmet>
        <title>AI Denial Management & Claims Recovery | MDeRCM</title>
        <meta 
          name="description" 
          content="AI denial management and claims recovery for healthcare. Reduce denials 75%, automate appeals and recover maximum revenue for your practice." 
        />
        <meta 
          name="keywords" 
          content="denial management, claims recovery, healthcare revenue cycle management services, medical revenue cycle management services, revenue cycle management consulting services, medical billing outsourcing services, best revenue cycle management companies, revenue cycle management services usa, hospital revenue cycle management, healthcare cost reduction strategies, how to improve revenue cycle management, outsourced revenue cycle management services, revenue cycle management services for hospitals, medical billing and coding outsourcing" 
        />
        <link rel="canonical" href="https://mdercm.com/healthcare/ai/denial-management" />
        <meta property="og:title" content="AI Denial Management & Claims Recovery | Healthcare Revenue Cycle Management" />
        <meta property="og:description" content="Turn denials into recoveries with AI-driven resolution. Reduce denial rates by 70% and recover revenue faster." />
        <meta property="og:url" content="https://mdercm.com/healthcare/ai/denial-management" />
        <meta property="og:type" content="website" />
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
      </Helmet>

      {/* BREADCRUMB NAVIGATION */}
      <div style={{ 
        padding: '20px 5%', 
        backgroundColor: '#f7f9fb',
        borderBottom: '1px solid #e5e7eb'
      }}>
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          flexWrap: 'wrap',
          gap: '8px',
          fontSize: '14px',
          color: '#666'
        }}>
          <Link 
            to="/" 
            style={{ 
              color: '#666', 
              textDecoration: 'none',
              transition: 'color 0.3s ease'
            }}
            onMouseOver={(e) => e.target.style.color = '#F26522'}
            onMouseOut={(e) => e.target.style.color = '#666'}
          >
            Home
          </Link>
          <span style={{ color: '#999' }}>/</span>
          <Link 
            to="/healthcare/ai" 
            style={{ 
              color: '#666', 
              textDecoration: 'none',
              transition: 'color 0.3s ease'
            }}
            onMouseOver={(e) => e.target.style.color = '#F26522'}
            onMouseOut={(e) => e.target.style.color = '#666'}
          >
            AI Solutions
          </Link>
          <span style={{ color: '#999' }}>/</span>
          <span style={{ color: '#F26522', fontWeight: '600' }}>Denial Management</span>
        </div>
      </div>

      {/* HERO SECTION */}
      <motion.div
        initial="hidden"
        animate="visible"
        variants={fadeIn}
        style={{
          backgroundImage: `linear-gradient(to right, rgba(0, 0, 0, 0.9), rgba(0, 0, 0, 0.6)), url(${heroImage})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          padding: '80px 5%',
          display: 'flex',
          flexWrap: 'wrap',
          justifyContent: 'space-between',
          alignItems: 'center',
          color: '#ffffff',
        }}
      >
        <div style={{ flex: '1 1 100%', maxWidth: '650px', zIndex: 2 }}>
          <motion.h1 style={{ fontSize: 'clamp(24px, 5vw, 42px)', fontWeight: 700, marginBottom: '20px', lineHeight: 1.4 }}>
            Healthcare Revenue Cycle Management Services - AI Denial Management & Claims Recovery
          </motion.h1>
          <motion.p style={{ fontSize: 'clamp(14px, 2vw, 18px)', marginBottom: '30px', color: '#dddddd' }}>
            Leading <Link to="/" style={{ color: '#F26522', textDecoration: 'none', fontWeight: '600' }}>revenue cycle management services company</Link> offering AI-driven denial management. Our <strong>medical revenue cycle management services</strong> instantly identify, prioritize, and resolve denials with proven <strong>healthcare cost reduction strategies</strong> that stop revenue leakage.
          </motion.p>

          <div style={{
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'center',
            gap: '30px',
            marginBottom: '30px'
          }}>
            {[
              ['70%', 'Drop in Denial Rates'],
              ['25x', 'Faster Resolution Time'],
              ['50x', 'Boost in First-Pass Recovery']
            ].map(([value, label], i) => (
              <motion.div
                key={i}
                custom={i}
                variants={fadeIn}
                style={{
                  flex: '1 1 160px',
                  maxWidth: '220px',
                  textAlign: 'center'
                }}
              >
                <div style={{ fontSize: '36px', fontWeight: '700', color: '#F26522' }}>{value}</div>
                <div style={{ fontSize: '16px', color: '#ffffff' }}>{label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.div>

      {/* INTRO SECTION */}
      <motion.div
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        variants={fadeIn}
        style={{ padding: '80px 5%', backgroundColor: '#ffffff' }}
      >
          <h2 style={{
          textAlign: 'center',
          fontSize: 'clamp(24px, 4vw, 36px)',
          fontWeight: '700',
          color: '#001F2B',
          marginBottom: '30px'
        }}>
          Best Revenue Cycle Management Companies - End Denial Guesswork
        </h2>

        <p style={{ 
          textAlign: 'center', 
          fontSize: '18px', 
          color: '#555', 
          marginBottom: '50px',
          maxWidth: '900px',
          margin: '0 auto 50px'
        }}>
          As one of the <strong>best revenue cycle management companies</strong>, we provide comprehensive <Link to="/" style={{ color: '#F26522', textDecoration: 'none' }}>revenue cycle management consulting services</Link> that include advanced AI denial management and <strong>medical billing outsourcing services</strong>.
        </p>

        <div style={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '40px'
        }}>
          <div style={{ flex: '1 1 500px', minWidth: '280px', maxWidth: '600px' }}>
            <h3 style={{ fontSize: '24px', fontWeight: '700', marginBottom: '20px', color: '#001F2B' }}>
              Hospital Revenue Cycle Management - Real-Time Denial Visibility
            </h3>
            <p style={{ fontSize: '16px', lineHeight: '1.8', color: '#333', marginBottom: '20px' }}>
              <Link to="/" style={{ color: '#F26522', textDecoration: 'none', fontWeight: '600' }}>MDeRCM</Link>'s Denial Management AI automatically reads remits, identifies denial reasons, and recommends next steps. Our <strong>outsourced revenue cycle management services</strong> handle everything from appeal letters to resubmission flows—no manual sorting required.
            </p>
            <p style={{ fontSize: '16px', lineHeight: '1.8', color: '#333', marginBottom: '20px' }}>
              Our <strong>revenue cycle management services for hospitals</strong> implement proven <strong>healthcare cost reduction strategies</strong> by automating denial detection and resolution. Learn <strong>how to improve revenue cycle management</strong> with intelligent AI that reduces denial rates by 70% and accelerates recovery time by 25x.
            </p>
            <p style={{ fontSize: '16px', lineHeight: '1.8', color: '#333' }}>
              <Link to="/" style={{ color: '#F26522', textDecoration: 'none' }}>MDeRCM</Link>'s <strong>medical billing and coding outsourcing</strong> services work seamlessly with our denial management system to create a comprehensive solution that maximizes revenue recovery and minimizes write-offs.
            </p>
          </div>
          <motion.div
            whileHover={{ scale: 1.02 }}
            transition={{ duration: 0.5 }}
            style={{
              flex: '1 1 400px',
              minWidth: '280px',
              maxWidth: '500px',
              borderRadius: '16px',
              overflow: 'hidden',
              boxShadow: '0 12px 40px rgba(0, 0, 0, 0.15)'
            }}
          >
            <img 
              src={claimImage} 
              alt="AI Denial Management Dashboard" 
              style={{ width: '100%', height: 'auto', display: 'block' }}
            />
          </motion.div>
        </div>
      </motion.div>

      {/* HOW IT WORKS SECTION */}
      <div style={{ padding: '80px 5%', backgroundColor: '#f7f9fb' }}>
        <motion.h2
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          style={{
            textAlign: 'center',
            fontSize: 'clamp(26px, 4vw, 38px)',
            fontWeight: '800',
            marginBottom: '20px',
            color: '#001F2B'
          }}
        >
          How Our AI Denial Management Works
        </motion.h2>

        <p style={{ 
          textAlign: 'center', 
          fontSize: '17px', 
          color: '#555', 
          marginBottom: '60px',
          maxWidth: '850px',
          margin: '0 auto 60px'
        }}>
          Our <strong>medical revenue cycle management services</strong> use advanced AI to automatically detect, analyze, and resolve claim denials.
        </p>

        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
          gap: '30px',
          maxWidth: '1200px',
          margin: '0 auto'
        }}>
          {[
            {
              step: '01',
              title: 'Automatic Detection',
              desc: 'AI monitors all incoming remittances and instantly identifies denied claims with detailed reason codes.',
              icon: '🔍'
            },
            {
              step: '02',
              title: 'Root Cause Analysis',
              desc: 'Machine learning analyzes denial patterns to identify systemic issues and prevent future denials.',
              icon: '🧠'
            },
            {
              step: '03',
              title: 'Prioritization & Routing',
              desc: 'Smart algorithms prioritize high-value denials and route them to appropriate specialists for resolution.',
              icon: '🎯'
            },
            {
              step: '04',
              title: 'Automated Resolution',
              desc: 'AI generates appeal letters, corrects claims, and resubmits with proper documentation automatically.',
              icon: '⚡'
            }
          ].map((item, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 60 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.15, duration: 0.6 }}
              style={{
                backgroundColor: '#ffffff',
                padding: '35px 25px',
                borderRadius: '12px',
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
                textAlign: 'center',
                position: 'relative',
                transition: 'all 0.3s ease'
              }}
              whileHover={{ 
                boxShadow: '0 8px 30px rgba(242, 101, 34, 0.2)',
                transform: 'translateY(-5px)'
              }}
            >
              <div style={{
                position: 'absolute',
                top: '15px',
                right: '15px',
                fontSize: '14px',
                fontWeight: '700',
                color: '#F26522',
                backgroundColor: '#FFF5F0',
                padding: '5px 12px',
                borderRadius: '20px'
              }}>
                {item.step}
              </div>
              <div style={{ fontSize: '48px', marginBottom: '15px' }}>{item.icon}</div>
              <h3 style={{ 
                fontSize: '20px', 
                fontWeight: '700', 
                color: '#001F2B', 
                marginBottom: '12px' 
              }}>
                {item.title}
              </h3>
              <p style={{ 
                fontSize: '15px', 
                color: '#666', 
                lineHeight: '1.6'
              }}>
                {item.desc}
              </p>
            </motion.div>
          ))}
        </div>
      </div>

      {/* KEY FEATURES SECTION - 5x3 GRID */}
      <div style={{ padding: '80px 5%', backgroundColor: '#ffffff' }}>
        <motion.h2
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          style={{
            textAlign: 'center',
            fontSize: 'clamp(26px, 4vw, 38px)',
            fontWeight: '800',
            marginBottom: '20px',
            color: '#001F2B'
          }}
        >
          Comprehensive Denial Management Features
        </motion.h2>

        <p style={{ 
          textAlign: 'center', 
          fontSize: '17px', 
          color: '#555', 
          marginBottom: '60px',
          maxWidth: '900px',
          margin: '0 auto 60px'
        }}>
          Advanced tools designed to minimize denials and maximize revenue recovery.
        </p>

        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
          gap: '25px',
          maxWidth: '1400px',
          margin: '0 auto'
        }}>
          {[
            {
              icon: '📊',
              title: 'Real-Time Denial Analytics',
              desc: 'Comprehensive dashboards showing denial trends, patterns, and recovery rates in real-time.'
            },
            {
              icon: '🤖',
              title: 'Intelligent Appeal Generation',
              desc: 'AI automatically creates compelling appeal letters with supporting documentation and evidence.'
            },
            {
              icon: '🔄',
              title: 'Automated Resubmission',
              desc: 'Corrected claims are automatically resubmitted with proper coding and documentation.'
            },
            {
              icon: '📝',
              title: 'Reason Code Library',
              desc: 'Extensive database of denial codes with resolution strategies and historical success rates.'
            },
            {
              icon: '⏱️',
              title: 'Timely Filing Tracking',
              desc: 'Automatic monitoring of appeal deadlines to ensure compliance with payer timelines.'
            },
            {
              icon: '💡',
              title: 'Predictive Prevention',
              desc: 'Machine learning identifies potential denials before submission for proactive correction.'
            },
            {
              icon: '📈',
              title: 'Performance Benchmarking',
              desc: 'Compare your denial rates and recovery performance against industry standards.'
            },
            {
              icon: '🎯',
              title: 'Priority Scoring',
              desc: 'Intelligent algorithms rank denials by recovery potential and time sensitivity.'
            },
            {
              icon: '🔐',
              title: 'Secure Documentation',
              desc: 'HIPAA-compliant storage and management of all appeal documentation and correspondence.'
            },
            {
              icon: '📞',
              title: 'Payer Communication',
              desc: 'Automated follow-up with insurance companies for status updates and additional requirements.'
            },
            {
              icon: '💼',
              title: 'Work Queue Management',
              desc: 'Organized workflow system that tracks each denial through the resolution process.'
            },
            {
              icon: '📋',
              title: 'Compliance Checking',
              desc: 'Automated verification that all appeals meet regulatory and payer-specific requirements.'
            },
            {
              icon: '🌐',
              title: 'Multi-Payer Integration',
              desc: 'Seamless integration with major insurance portals for direct submission and tracking.'
            },
            {
              icon: '📱',
              title: 'Mobile Access',
              desc: 'Review and manage denials from any device with our responsive mobile interface.'
            },
            {
              icon: '🎓',
              title: 'Staff Training Tools',
              desc: 'Built-in educational resources to help your team understand and prevent common denials.'
            }
          ].map((feature, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.05, duration: 0.5 }}
              style={{
                backgroundColor: '#f7f9fb',
                padding: '30px 20px',
                borderRadius: '12px',
                textAlign: 'center',
                border: '2px solid #e5e7eb',
                transition: 'all 0.3s ease'
              }}
              whileHover={{ 
                borderColor: '#F26522',
                backgroundColor: '#ffffff',
                boxShadow: '0 8px 25px rgba(242, 101, 34, 0.15)',
                transform: 'translateY(-5px)'
              }}
            >
              <div style={{ fontSize: '42px', marginBottom: '15px' }}>{feature.icon}</div>
              <h3 style={{ 
                fontSize: '18px', 
                fontWeight: '700', 
                color: '#001F2B', 
                marginBottom: '10px' 
              }}>
                {feature.title}
              </h3>
              <p style={{ 
                fontSize: '14px', 
                color: '#666', 
                lineHeight: '1.6'
              }}>
                {feature.desc}
              </p>
            </motion.div>
          ))}
        </div>
      </div>

      {/* BENEFITS SECTION - 4x2 GRID */}
      <div style={{ padding: '80px 5%', backgroundColor: '#f7f9fb' }}>
        <motion.h2
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          style={{
            textAlign: 'center',
            fontSize: 'clamp(26px, 4vw, 38px)',
            fontWeight: '800',
            marginBottom: '20px',
            color: '#001F2B'
          }}
        >
          Benefits of AI Denial Management
        </motion.h2>

        <p style={{ 
          textAlign: 'center', 
          fontSize: '17px', 
          color: '#555', 
          marginBottom: '60px',
          maxWidth: '850px',
          margin: '0 auto 60px'
        }}>
          Transform your denial management process with AI-powered automation and intelligence.
        </p>

        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
          gap: '30px',
          maxWidth: '1300px',
          margin: '0 auto'
        }}>
          {[
            {
              icon: '💰',
              title: 'Maximize Revenue Recovery',
              desc: 'Recover up to 50% more denied claims with AI-optimized appeals and faster resolution times.'
            },
            {
              icon: '⚡',
              title: 'Reduce Resolution Time',
              desc: 'Cut denial resolution time by 25x with automated workflows and intelligent prioritization.'
            },
            {
              icon: '📉',
              title: 'Lower Denial Rates',
              desc: 'Prevent future denials by identifying root causes and implementing corrective measures.'
            },
            {
              icon: '🎯',
              title: 'Improve First-Pass Rate',
              desc: 'Increase clean claim rate by 40% through predictive analysis and pre-submission validation.'
            },
            {
              icon: '👥',
              title: 'Reduce Staff Burden',
              desc: 'Free up staff time by automating 80% of routine denial management tasks.'
            },
            {
              icon: '📊',
              title: 'Better Visibility',
              desc: 'Gain complete insight into denial trends, root causes, and recovery performance metrics.'
            },
            {
              icon: '🔒',
              title: 'Enhanced Compliance',
              desc: 'Ensure all appeals meet regulatory requirements and payer-specific guidelines automatically.'
            },
            {
              icon: '💡',
              title: 'Continuous Improvement',
              desc: 'Machine learning continuously optimizes denial prevention and resolution strategies.'
            }
          ].map((benefit, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 60 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1, duration: 0.6 }}
              style={{
                backgroundColor: '#ffffff',
                padding: '35px 25px',
                borderRadius: '12px',
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
                textAlign: 'center',
                transition: 'all 0.3s ease'
              }}
              whileHover={{ 
                boxShadow: '0 8px 30px rgba(242, 101, 34, 0.2)',
                transform: 'translateY(-5px)'
              }}
            >
              <div style={{ fontSize: '48px', marginBottom: '15px' }}>{benefit.icon}</div>
              <h3 style={{ 
                fontSize: '20px', 
                fontWeight: '700', 
                color: '#001F2B', 
                marginBottom: '12px' 
              }}>
                {benefit.title}
              </h3>
              <p style={{ 
                fontSize: '15px', 
                color: '#666', 
                lineHeight: '1.6'
              }}>
                {benefit.desc}
              </p>
            </motion.div>
          ))}
        </div>
      </div>

      {/* COMMON DENIAL REASONS SECTION - 4x4 GRID */}
      <div style={{ padding: '80px 5%', backgroundColor: '#ffffff' }}>
        <motion.h2
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          style={{
            textAlign: 'center',
            fontSize: 'clamp(26px, 4vw, 38px)',
            fontWeight: '800',
            marginBottom: '20px',
            color: '#001F2B'
          }}
        >
          Common Denial Reasons We Resolve
        </motion.h2>

        <p style={{ 
          textAlign: 'center', 
          fontSize: '17px', 
          color: '#555', 
          marginBottom: '60px',
          maxWidth: '900px',
          margin: '0 auto 60px'
        }}>
          Our AI system is trained to handle all types of claim denials with proven resolution strategies.
        </p>

        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
          gap: '25px',
          maxWidth: '1400px',
          margin: '0 auto'
        }}>
          {[
            {
              icon: '🔍',
              title: 'Missing Information',
              desc: 'Automatically identifies and adds missing patient demographics, authorization numbers, and documentation.'
            },
            {
              icon: '📋',
              title: 'Coding Errors',
              desc: 'Detects and corrects ICD-10, CPT, and modifier errors that trigger denials.'
            },
            {
              icon: '⏰',
              title: 'Timely Filing',
              desc: 'Tracks submission deadlines and ensures claims are filed within payer time limits.'
            },
            {
              icon: '🔐',
              title: 'Authorization Issues',
              desc: 'Resolves missing, expired, or incorrect prior authorization denials with automated follow-up.'
            },
            {
              icon: '🚫',
              title: 'Medical Necessity',
              desc: 'Generates compelling appeals with clinical documentation to prove medical necessity.'
            },
            {
              icon: '👤',
              title: 'Eligibility Problems',
              desc: 'Addresses patient eligibility issues including inactive coverage and policy changes.'
            },
            {
              icon: '💳',
              title: 'Duplicate Claims',
              desc: 'Identifies and resolves duplicate billing issues with proper claim resubmission.'
            },
            {
              icon: '📝',
              title: 'Documentation Deficiencies',
              desc: 'Automatically requests and attaches missing medical records and supporting documents.'
            },
            {
              icon: '🏥',
              title: 'Non-Covered Services',
              desc: 'Appeals non-covered denials with policy review and alternative billing strategies.'
            },
            {
              icon: '💰',
              title: 'Coordination of Benefits',
              desc: 'Resolves COB issues by identifying primary/secondary payer responsibilities correctly.'
            },
            {
              icon: '⚠️',
              title: 'Bundling Issues',
              desc: 'Corrects unbundling and bundling errors with proper modifier usage and code selection.'
            },
            {
              icon: '📅',
              title: 'Service Date Errors',
              desc: 'Fixes date of service discrepancies and claim timing issues automatically.'
            },
            {
              icon: '🎯',
              title: 'Provider Credentialing',
              desc: 'Addresses provider enrollment and credentialing issues causing claim rejections.'
            },
            {
              icon: '🔄',
              title: 'Reimbursement Disputes',
              desc: 'Negotiates and appeals incorrect payment amounts with contract rate verification.'
            },
            {
              icon: '📑',
              title: 'Claim Corrections',
              desc: 'Submits corrected claims with proper voiding and resubmission procedures.'
            }
          ].map((reason, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.03, duration: 0.5 }}
              style={{
                backgroundColor: '#f7f9fb',
                padding: '30px 20px',
                borderRadius: '12px',
                border: '2px solid #e5e7eb',
                textAlign: 'center',
                transition: 'all 0.3s ease'
              }}
              whileHover={{ 
                borderColor: '#F26522',
                backgroundColor: '#ffffff',
                boxShadow: '0 8px 25px rgba(242, 101, 34, 0.15)',
                transform: 'translateY(-5px)'
              }}
            >
              <div style={{ fontSize: '42px', marginBottom: '15px' }}>{reason.icon}</div>
              <h3 style={{ 
                fontSize: '18px', 
                fontWeight: '700', 
                color: '#001F2B', 
                marginBottom: '10px' 
              }}>
                {reason.title}
              </h3>
              <p style={{ 
                fontSize: '14px', 
                color: '#666', 
                lineHeight: '1.6'
              }}>
                {reason.desc}
              </p>
            </motion.div>
          ))}
        </div>
      </div>

      {/* RELATED SERVICES - 5x3 GRID */}
      <div style={{ padding: '80px 5%', backgroundColor: '#f7f9fb' }}>
        <motion.h2
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          style={{
            textAlign: 'center',
            fontSize: 'clamp(26px, 4vw, 38px)',
            fontWeight: '800',
            marginBottom: '20px',
            color: '#001F2B'
          }}
        >
          Complete Revenue Cycle Management Services
        </motion.h2>

        <p style={{ 
          textAlign: 'center', 
          fontSize: '17px', 
          color: '#555', 
          marginBottom: '60px',
          maxWidth: '900px',
          margin: '0 auto 60px'
        }}>
          Explore our comprehensive suite of AI-powered healthcare revenue cycle management solutions.
        </p>

        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
          gap: '25px',
          maxWidth: '1400px',
          margin: '0 auto'
        }}>
          {[
            {
              icon: '✅',
              title: 'Eligibility Check',
              desc: 'Real-time insurance verification and eligibility checks to prevent claim denials.',
              link: '/healthcare/ai/eligibility-check'
            },
            {
              icon: '📋',
              title: 'Policy Status Verification',
              desc: 'Automated verification of patient insurance policy status and coverage details.',
              link: '/healthcare/ai/policy-status-verification'
            },
            {
              icon: '📝',
              title: 'Prior Authorization',
              desc: 'Streamlined prior authorization process with automated submission and tracking.',
              link: '/healthcare/ai/prior-authorization'
            },
            {
              icon: '💲',
              title: 'Insurance Contract Repricing',
              desc: 'Intelligent contract repricing to maximize reimbursements from insurance payers.',
              link: '/healthcare/ai/InsuranceContract-Repricing'
            },
            {
              icon: '📱',
              title: 'Patient Intake',
              desc: 'AI-powered payment reminders and patient collections to improve cash flow.',
              link: '/healthcare/ai/patient-intake'
            },
            {
              icon: '💳',
              title: 'Payment Posting',
              desc: 'Automated payment posting with accurate reconciliation and reporting.',
              link: '/healthcare/ai/payment-posting'
            },
            {
              icon: '📊',
              title: 'Accounts Receivable',
              desc: 'Comprehensive AR management with automated follow-up and collections.',
              link: '/healthcare/ai/accounts-receivable'
            },
            {
              icon: '🛡️',
              title: 'Compliance Agent',
              desc: 'Ensure HIPAA compliance and regulatory adherence across all operations.',
              link: '/healthcare/ai/compliance-agent'
            },
            {
              icon: '🦷',
              title: 'Dental RCM',
              desc: 'Specialized revenue cycle management solutions for dental practices.',
              link: '/Dental'
            },
            {
              icon: '🧠',
              title: 'Mental Health RCM',
              desc: 'Tailored billing and revenue cycle services for mental health providers.',
              link: '/MentalHealth'
            },
            {
              icon: '📑',
              title: 'Compliance Services',
              desc: 'Complete compliance management and risk assessment services.',
              link: '/compliance'
            },
            {
              icon: '📰',
              title: 'Industry News & Insights',
              desc: 'Stay updated with latest healthcare RCM trends and best practices.',
              link: '/blog'
            },
            {
              icon: '🎓',
              title: 'Denial Code Resources',
              desc: 'Comprehensive guide to insurance denial codes and resolution strategies.',
              link: '/resources/denial-codes'
            },
            {
              icon: '📚',
              title: 'RCM Resources',
              desc: 'Educational resources and tools for healthcare revenue cycle optimization.',
              link: '/resources'
            },
            {
              icon: '🎯',
              title: 'Why Choose Us',
              desc: 'Discover why healthcare providers trust MDeRCM for their revenue cycle needs.',
              link: '/why'
            }
          ].map((service, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.03, duration: 0.5 }}
              style={{
                backgroundColor: '#ffffff',
                padding: '30px 20px',
                borderRadius: '12px',
                border: '2px solid #e5e7eb',
                textAlign: 'center',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                minHeight: '250px',
                transition: 'all 0.3s ease'
              }}
              whileHover={{ 
                borderColor: '#F26522',
                backgroundColor: '#f7f9fb',
                boxShadow: '0 8px 25px rgba(242, 101, 34, 0.15)',
                transform: 'translateY(-5px)'
              }}
            >
              <div>
                <div style={{ fontSize: '42px', marginBottom: '15px' }}>{service.icon}</div>
                <h3 style={{ 
                  fontSize: '18px', 
                  fontWeight: '700', 
                  color: '#001F2B', 
                  marginBottom: '12px' 
                }}>
                  {service.title}
                </h3>
                <p style={{ 
                  fontSize: '14px', 
                  color: '#666', 
                  lineHeight: '1.6',
                  marginBottom: '15px'
                }}>
                  {service.desc}
                </p>
              </div>
              <Link 
                to={service.link}
                style={{ 
                  color: '#F26522', 
                  textDecoration: 'none', 
                  fontWeight: '600',
                  fontSize: '15px',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  transition: 'all 0.3s ease'
                }}
              >
                Learn More →
              </Link>
            </motion.div>
          ))}
        </div>
      </div>

      {/* WHY CHOOSE MDERCM - 5x3 GRID */}
      <div style={{ 
        padding: '80px 5%', 
        backgroundColor: '#001F2B',
        color: '#ffffff'
      }}>
        <motion.h2
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          style={{
            textAlign: 'center',
            fontSize: 'clamp(26px, 4vw, 38px)',
            fontWeight: '800',
            marginBottom: '30px',
            color: '#ffffff'
          }}
        >
          Why Choose MDeRCM for Outsourced Revenue Cycle Management Services?
        </motion.h2>

        <p style={{ 
          textAlign: 'center', 
          fontSize: '17px', 
          color: '#dddddd', 
          marginBottom: '60px',
          maxWidth: '900px',
          margin: '0 auto 60px',
          lineHeight: '1.8'
        }}>
          As one of the <strong>best revenue cycle management companies</strong>, <Link to="/" style={{ color: '#F26522', textDecoration: 'none', fontWeight: '600' }}>MDeRCM</Link> provides industry-leading <strong>revenue cycle management services USA</strong> with proven results and dedicated support.
        </p>

        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
          gap: '25px',
          maxWidth: '1400px',
          margin: '0 auto'
        }}>
          {[
            {
              icon: '🏆',
              title: 'Industry-Leading Expertise',
              desc: 'Over 10+ years of experience in healthcare revenue cycle management with proven track record of success.'
            },
            {
              icon: '🤖',
              title: 'Advanced AI Technology',
              desc: 'Cutting-edge AI and machine learning algorithms that continuously improve accuracy and efficiency.'
            },
            {
              icon: '💵',
              title: 'Maximize Revenue',
              desc: 'Increase revenue by up to 30% through optimized denial management, reduced write-offs, and faster recovery.'
            },
            {
              icon: '⚡',
              title: '24/7 Support',
              desc: 'Round-the-clock dedicated support team ensuring seamless operations and quick issue resolution.'
            },
            {
              icon: '🔒',
              title: 'HIPAA Compliant',
              desc: 'Fully compliant with HIPAA and industry regulations, ensuring data security and patient privacy.'
            },
            {
              icon: '📈',
              title: 'Scalable Solutions',
              desc: 'Flexible services that grow with your practice, from small clinics to large hospital systems.'
            },
            {
              icon: '💡',
              title: 'Transparent Reporting',
              desc: 'Real-time dashboards and detailed analytics providing complete visibility into your revenue cycle.'
            },
            {
              icon: '🎯',
              title: 'Proven ROI',
              desc: 'Measurable results with most clients seeing positive ROI within 3-6 months of implementation.'
            },
            {
              icon: '🌟',
              title: 'Client Satisfaction',
              desc: '98% client satisfaction rate with dedicated account managers and personalized service.'
            },
            {
              icon: '🔄',
              title: 'Seamless Integration',
              desc: 'Easy integration with all major EHR systems and practice management software.'
            },
            {
              icon: '💼',
              title: 'Expert Team',
              desc: 'Certified medical billing specialists and revenue cycle experts managing your accounts.'
            },
            {
              icon: '🎓',
              title: 'Continuous Training',
              desc: 'Regular staff training on latest billing codes, compliance updates, and best practices.'
            },
            {
              icon: '🏥',
              title: 'Multi-Specialty Expertise',
              desc: 'Experience across 50+ medical specialties including dental, mental health, and more.'
            },
            {
              icon: '📞',
              title: 'Dedicated Account Manager',
              desc: 'Personal account manager assigned to oversee your revenue cycle operations.'
            },
            {
              icon: '🚀',
              title: 'Fast Implementation',
              desc: 'Quick onboarding process with go-live in as little as 2-4 weeks.'
            }
          ].map((reason, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 60 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.05, duration: 0.6 }}
              style={{
                backgroundColor: 'rgba(255, 255, 255, 0.05)',
                padding: '25px 20px',
                borderRadius: '12px',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                textAlign: 'center',
                transition: 'all 0.3s ease'
              }}
              whileHover={{ 
                backgroundColor: 'rgba(242, 101, 34, 0.1)',
                borderColor: '#F26522',
                transform: 'translateY(-5px)'
              }}
            >
              <div style={{ fontSize: '42px', marginBottom: '12px' }}>{reason.icon}</div>
              <h3 style={{ 
                fontSize: '18px', 
                fontWeight: '700', 
                color: '#ffffff', 
                marginBottom: '10px' 
              }}>
                {reason.title}
              </h3>
              <p style={{ 
                fontSize: '14px', 
                color: '#dddddd', 
                lineHeight: '1.6'
              }}>
                {reason.desc}
              </p>
            </motion.div>
          ))}
        </div>

        <div style={{
          textAlign: 'center',
          marginTop: '60px'
        }}>
          <Link 
            to="/free-trial"
            style={{
              backgroundColor: '#F26522',
              color: '#ffffff',
              padding: '18px 50px',
              borderRadius: '8px',
              textDecoration: 'none',
              fontWeight: '700',
              fontSize: '18px',
              display: 'inline-block',
              transition: 'all 0.3s ease',
              boxShadow: '0 6px 20px rgba(242, 101, 34, 0.4)'
            }}
            onMouseOver={(e) => {
              e.target.style.backgroundColor = '#D94F1C';
              e.target.style.transform = 'translateY(-2px)';
              e.target.style.boxShadow = '0 8px 25px rgba(242, 101, 34, 0.5)';
            }}
            onMouseOut={(e) => {
              e.target.style.backgroundColor = '#F26522';
              e.target.style.transform = 'translateY(0)';
              e.target.style.boxShadow = '0 6px 20px rgba(242, 101, 34, 0.4)';
            }}
          >
            Start Your Free Trial Today
          </Link>
        </div>
      </div>

      <FAQSection />
      <ConsultationCTA />
    </>
  );
};

export default DenialManagement;